import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Spinner,
} from "reactstrap";

import {
  ChecklistStepStatus,
  ClinicalProtocolChecklistProgress,
  ClinicalProtocolChecklistStep,
  getSessionChecklist,
  markAllSessionChecklist,
  updateSessionChecklistStep,
} from "../../services/clinicalChecklistService";
import { ClinicalProtocol, ClinicalProtocolSession } from "../../services/clinicalProtocolService";

type Props = {
  protocol: ClinicalProtocol;
  canEdit: boolean;
};

const statusColor = (status: ChecklistStepStatus): string => {
  switch (status) {
    case "done":
      return "success";
    case "skipped":
      return "secondary";
    default:
      return "warning";
  }
};

const ClinicalProtocolChecklistPanel: React.FC<Props> = ({ protocol, canEdit }) => {
  const { t } = useTranslation();
  const sessions = useMemo(() => protocol.sessions ?? [], [protocol.sessions]);
  const [sessionId, setSessionId] = useState<number | "">(
    sessions[0]?.id ?? ""
  );
  const [steps, setSteps] = useState<ClinicalProtocolChecklistStep[]>([]);
  const [progress, setProgress] = useState<ClinicalProtocolChecklistProgress | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [busyStepId, setBusyStepId] = useState<number | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => {
    if (!sessionId && sessions[0]?.id) {
      setSessionId(sessions[0].id);
    }
  }, [sessionId, sessions]);

  const load = useCallback(async () => {
    if (!sessionId) {
      setSteps([]);
      setProgress(null);
      return;
    }
    setLoading(true);
    try {
      const response = await getSessionChecklist(protocol.id, Number(sessionId));
      setSteps(response.data?.steps ?? []);
      setProgress(response.data?.progress ?? null);
    } catch (error: any) {
      toast.error(error?.data || t("clients.protocol.checklist.loadError"));
      setSteps([]);
      setProgress(null);
    } finally {
      setLoading(false);
    }
  }, [protocol.id, sessionId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyPayload = (payload: {
    steps: ClinicalProtocolChecklistStep[];
    progress: ClinicalProtocolChecklistProgress;
  }) => {
    setSteps(payload.steps ?? []);
    setProgress(payload.progress ?? null);
  };

  const handleStatus = async (step: ClinicalProtocolChecklistStep, status: ChecklistStepStatus) => {
    if (!sessionId || !canEdit) {
      return;
    }
    setBusyStepId(step.id);
    try {
      const response = await updateSessionChecklistStep(
        protocol.id,
        Number(sessionId),
        step.id,
        { status }
      );
      applyPayload({
        steps: response.data.steps,
        progress: response.data.progress,
      });
    } catch (error: any) {
      toast.error(error?.data || t("clients.protocol.checklist.saveError"));
    } finally {
      setBusyStepId(null);
    }
  };

  const handleMarkAll = async () => {
    if (!sessionId || !canEdit) {
      return;
    }
    setMarkingAll(true);
    try {
      const response = await markAllSessionChecklist(protocol.id, Number(sessionId), {
        status: "done",
      });
      applyPayload(response.data);
      toast.success(response.message || t("clients.protocol.checklist.markAllSuccess"));
    } catch (error: any) {
      toast.error(error?.data || t("clients.protocol.checklist.saveError"));
    } finally {
      setMarkingAll(false);
    }
  };

  const selectedSession: ClinicalProtocolSession | undefined = sessions.find(
    (item) => item.id === Number(sessionId)
  );

  return (
    <div className="mt-3 border rounded p-3 bg-light-subtle">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
        <div>
          <h6 className="mb-1">{t("clients.protocol.checklist.title")}</h6>
          <p className="text-muted small mb-0">
            {t("clients.protocol.checklist.hint")}
          </p>
        </div>
        {canEdit ? (
          <Link to="/settings/clinical-checklist-templates" className="small">
            {t("clients.protocol.checklist.manageTemplates")}
          </Link>
        ) : null}
      </div>

      <FormGroup className="mb-2" style={{ maxWidth: 280 }}>
        <Label for={`checklist-session-${protocol.id}`}>
          {t("clients.protocol.checklist.sessionSelect")}
        </Label>
        <Input
          id={`checklist-session-${protocol.id}`}
          type="select"
          value={sessionId}
          onChange={(e) =>
            setSessionId(e.target.value ? Number(e.target.value) : "")
          }
        >
          {sessions.map((session) => (
            <option key={session.id} value={session.id}>
              {t("clients.protocol.sessionOf", {
                n: session.session_number,
                m: protocol.total_sessions,
              })}
            </option>
          ))}
        </Input>
      </FormGroup>

      {progress ? (
        <p className="small text-muted mb-2">
          {t("clients.protocol.checklist.progress", {
            done: progress.done + progress.skipped,
            total: progress.total,
          })}
          {selectedSession
            ? ` · ${t("clients.protocol.sessionOf", {
                n: selectedSession.session_number,
                m: protocol.total_sessions,
              })}`
            : ""}
        </p>
      ) : null}

      {loading ? (
        <div className="py-3 text-center">
          <Spinner size="sm" />
        </div>
      ) : steps.length === 0 ? (
        <p className="small text-muted mb-0">
          {t("clients.protocol.checklist.empty")}
        </p>
      ) : (
        <>
          <ul className="list-unstyled mb-2">
            {steps.map((step) => (
              <li
                key={step.id}
                className="d-flex flex-wrap align-items-start justify-content-between gap-2 border-bottom py-2"
              >
                <div className="flex-grow-1" style={{ minWidth: 180 }}>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="fw-medium">{step.title}</span>
                    <Badge color={statusColor(step.status)}>
                      {t(`clients.protocol.checklist.status.${step.status}`)}
                    </Badge>
                    {step.phase ? (
                      <Badge color="soft-info" className="text-info">
                        {t(`clients.protocol.checklist.phase.${step.phase}`)}
                      </Badge>
                    ) : null}
                  </div>
                  {step.description ? (
                    <p className="small text-muted mb-0 mt-1">{step.description}</p>
                  ) : null}
                </div>
                {canEdit && protocol.status === "active" ? (
                  <div className="d-inline-flex gap-1 flex-wrap">
                    {step.status !== "done" ? (
                      <Button
                        color="success"
                        size="sm"
                        disabled={busyStepId === step.id}
                        onClick={() => void handleStatus(step, "done")}
                      >
                        {t("clients.protocol.checklist.markDone")}
                      </Button>
                    ) : null}
                    {step.status !== "skipped" ? (
                      <Button
                        color="light"
                        size="sm"
                        disabled={busyStepId === step.id}
                        onClick={() => void handleStatus(step, "skipped")}
                      >
                        {t("clients.protocol.checklist.markSkipped")}
                      </Button>
                    ) : null}
                    {step.status !== "pending" ? (
                      <Button
                        color="link"
                        size="sm"
                        className="p-0"
                        disabled={busyStepId === step.id}
                        onClick={() => void handleStatus(step, "pending")}
                      >
                        {t("clients.protocol.checklist.reopen")}
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          {canEdit && protocol.status === "active" && progress && progress.pending > 0 ? (
            <Button
              color="soft-success"
              size="sm"
              disabled={markingAll}
              onClick={() => void handleMarkAll()}
            >
              {markingAll
                ? t("clients.protocol.checklist.saving")
                : t("clients.protocol.checklist.markAll")}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
};

export default ClinicalProtocolChecklistPanel;
